import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ProjectCosting from "@/components/ProjectCosting";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, FileImage, Trash2, ClipboardList, ChevronRight, AlertTriangle } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow, Spinner } from "@/components/tac";
import PinCanvas from "@/components/PinCanvas";
import ProjectConstruction from "@/components/ProjectConstruction";
import ProjectHandover from "@/components/ProjectHandover";

const PHASES = [
  { key: "pre_construction", label: "Pre-Construction" },
  { key: "construction", label: "Construction" },
  { key: "post_construction", label: "Post-Construction" },
];

// Downscale large images client-side so blueprint uploads stay under proxy limits.
function fileToDownscaledB64(file, maxDim = 2000, quality = 0.85) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      const r = new FileReader();
      r.onload = () => resolve({ data: r.result.split(",")[1], content_type: file.type });
      r.onerror = reject; r.readAsDataURL(file); return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * scale); height = Math.round(height * scale);
      const c = document.createElement("canvas"); c.width = width; c.height = height;
      c.getContext("2d").drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve({ data: c.toDataURL("image/jpeg", quality).split(",")[1], content_type: "image/jpeg" });
    };
    img.onerror = reject; img.src = url;
  });
}

export default function ProjectDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = ["owner", "dispatcher"].includes(user?.role);
  const fileRef = useRef(null);
  const [activeBp, setActiveBp] = useState(null);
  const [view, setView] = useState(null);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ["project", id], queryFn: async () => (await api.get(`/projects/${id}`)).data });
  const { data: catalog } = useQuery({ queryKey: ["pin-catalog"], queryFn: async () => (await api.get("/pin-catalog")).data });

  const refresh = () => qc.invalidateQueries({ queryKey: ["project", id] });

  const [wasteInput, setWasteInput] = useState(0);
  const [clashNotes, setClashNotes] = useState({});
  useEffect(() => { if (data?.takeoff) setWasteInput(data.takeoff.waste_pct ?? 0); }, [data?.takeoff?.waste_pct]); // eslint-disable-line

  const saveWaste = async () => {
    const w = Math.max(0, Math.min(50, parseFloat(wasteInput) || 0));
    try { await api.patch(`/projects/${id}`, { name: p.name, address: p.address, gc_name: p.gc_name, waste_pct: w }); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const resolveClash = async (clash_key, status, note) => {
    try { await api.post(`/projects/${id}/clashes/resolve`, { clash_key, status, note: note ?? "" }); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const { data: enc } = { data: await fileToDownscaledB64(file) };
      await api.post(`/projects/${id}/blueprints`, { name: file.name.replace(/\.[^.]+$/, ""), filename: file.name, data: enc.data, content_type: enc.content_type });
      toast.success("Blueprint uploaded"); refresh();
    } catch (e2) { toast.error(apiErr(e2.response?.data?.detail) || "Upload failed"); }
    setUploading(false); if (fileRef.current) fileRef.current.value = "";
  };

  const deleteBp = async (bpId) => {
    try { await api.delete(`/blueprints/${bpId}`); toast.success("Blueprint removed"); if (activeBp?.id === bpId) setActiveBp(null); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const setPhase = async (status) => {
    try { await api.patch(`/projects/${id}`, { name: p.name, address: p.address, gc_name: p.gc_name, status }); toast.success("Phase updated"); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (isLoading) return <div className="p-10"><Spinner label="Loading project" /></div>;
  const p = data.project;
  const blueprints = data.blueprints || [];
  const takeoff = data.takeoff || { trades: [], grand_total: 0 };
  const clashes = data.clashes || [];
  const activeView = view || p.status;
  const current = activeBp ? blueprints.find((b) => b.id === activeBp.id) : blueprints[0];

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/projects" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Projects</Link>
      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div><Eyebrow>{p.gc_name ? `GC: ${p.gc_name}` : "Project"}</Eyebrow>
          <h1 className="mt-1 font-display text-3xl font-bold text-white" data-testid="project-title">{p.name}</h1>
          {p.address && <p className="mt-1 text-sm text-slate-400">{p.address}</p>}</div>
      </div>

      <ProjectCosting projectId={id} />

      {/* Phase stepper */}
      <div className="mt-6 flex items-center gap-2" data-testid="phase-stepper">
        {PHASES.map((ph, i) => (
          <div key={ph.key} className="flex items-center gap-2">
            <button data-testid={`phase-${ph.key}`} onClick={() => { setView(ph.key); if (canEdit && p.status !== ph.key) setPhase(ph.key); }}
              className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${activeView === ph.key ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-400 hover:text-white"}`}>{ph.label}</button>
            {i < PHASES.length - 1 && <ChevronRight className="h-4 w-4 text-slate-700" />}
          </div>
        ))}
      </div>

      {activeView === "pre_construction" && (<>
      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_300px]">
        {/* Blueprints + canvas */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><FileImage className="h-4 w-4 text-amber-400" /> Blueprints</h2>
            {canEdit && (
              <>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="blueprint-file-input" />
                <button data-testid="upload-blueprint-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-white hover:border-amber-500 disabled:opacity-50"><Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload"}</button>
              </>
            )}
          </div>

          {blueprints.length ? (
            <>
              <div className="mb-4 flex flex-wrap gap-2">
                {blueprints.map((b) => (
                  <div key={b.id} className={`group flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs ${current?.id === b.id ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-slate-800 text-slate-400"}`}>
                    <button data-testid={`select-blueprint-${b.id}`} onClick={() => setActiveBp(b)}>{b.name} <span className="text-slate-600">({(b.pins || []).length})</span></button>
                    {canEdit && <button onClick={() => deleteBp(b.id)} className="text-slate-600 hover:text-rose-400" data-testid={`delete-blueprint-${b.id}`}><Trash2 className="h-3 w-3" /></button>}
                  </div>
                ))}
              </div>
              {current && catalog && (
                <PinCanvas key={current.id} blueprintId={current.id} catalog={catalog}
                  initialPins={current.pins || []} initialRuns={current.runs || []} initialScale={current.scale || null}
                  clashes={clashes} contentType={current.content_type} onSaved={refresh} />
              )}
            </>
          ) : (
            <div className="card-tac flex flex-col items-center gap-3 py-16 text-center text-slate-500" data-testid="blueprints-empty">
              <FileImage className="h-8 w-8 text-slate-700" /> No blueprints yet. {canEdit ? "Upload a floor plan image to start pinning." : ""}
            </div>
          )}
        </div>

        {/* Takeoff */}
        <div>
          <h2 className="mb-3 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><ClipboardList className="h-4 w-4 text-cyan-400" /> Material takeoff</h2>
          <div className="card-tac p-4" data-testid="takeoff-panel">
            <div className="mb-3 flex items-baseline justify-between border-b border-slate-800 pb-3">
              <span className="text-sm text-slate-400">Total points</span>
              <span className="font-display text-2xl font-bold text-white" data-testid="takeoff-total">{takeoff.grand_total}</span>
            </div>
            {takeoff.linear_total_ft > 0 && (
              <div className="mb-3 flex items-baseline justify-between border-b border-slate-800 pb-3">
                <span className="text-sm text-slate-400">Wire / pipe / duct{takeoff.waste_pct > 0 ? ` · +${takeoff.waste_pct}%` : ""}</span>
                <span className="font-display text-xl font-bold text-cyan-300" data-testid="takeoff-linear-total">{takeoff.linear_total_ft} ft</span>
              </div>
            )}
            {(takeoff.linear_total_ft > 0 || canEdit) && (
              <div className="mb-3 flex items-center justify-between border-b border-slate-800 pb-3">
                <span className="text-xs text-slate-400">Waste / slack factor</span>
                {canEdit ? (
                  <div className="flex items-center gap-1">
                    <input data-testid="waste-input" type="number" min="0" max="50" value={wasteInput}
                      onChange={(e) => setWasteInput(e.target.value)} onBlur={saveWaste}
                      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                      className="w-14 rounded border border-slate-700 bg-[#0d1420] px-2 py-1 text-right text-xs text-white focus:border-cyan-500 focus:outline-none" />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                ) : <span className="font-mono-tac text-xs text-slate-300">{takeoff.waste_pct}%</span>}
              </div>
            )}
            {takeoff.trades.length ? takeoff.trades.map((t) => (
              <div key={t.trade} className="mb-4" data-testid={`takeoff-${t.trade}`}>
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} />
                  <span className="text-xs font-semibold text-white">{t.label}</span>
                  <span className="font-mono-tac text-[10px] text-slate-500">{t.subtotal}{t.linear_ft ? ` · ${t.linear_ft} ft` : ""}</span>
                </div>
                {t.items.map((it) => (
                  <div key={it.type} className="flex items-center justify-between py-0.5 pl-4 text-xs text-slate-400">
                    <span>{it.material}</span><span className={`font-mono-tac ${it.linear ? "text-cyan-300" : "text-slate-300"}`}>{it.count} {it.unit}</span>
                  </div>
                ))}
              </div>
            )) : <p className="py-6 text-center text-xs text-slate-600">Drop pins or draw runs to generate a bill of materials.</p>}
          </div>
        </div>
      </div>
      {clashes.length > 0 && (
        <div className="card-tac mt-4 p-4" data-testid="clash-panel">
          <h3 className="mb-2 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-rose-400"><AlertTriangle className="h-4 w-4" /> Clash warnings ({clashes.filter((c) => (c.status || "open") === "open").length} open / {clashes.length})</h3>
          {clashes.slice(0, 15).map((c, i) => {
            const st = c.status || "open";
            const noteVal = clashNotes[c.clash_key] !== undefined ? clashNotes[c.clash_key] : (c.note || "");
            return (
              <div key={c.clash_key} data-testid={`clash-${i}`} className="border-b border-slate-800 py-2 text-xs">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-2 w-2 rounded-full ${c.severity === "high" ? "bg-rose-400" : "bg-amber-400"}`} />
                    <span className="text-slate-300">{c.a.trade}/{c.a.type} ✕ {c.b.trade}/{c.b.type}</span>
                    <span className="font-mono-tac text-[10px] text-slate-600">{c.blueprint_name}</span>
                    <span data-testid={`clash-status-${i}`} className={`rounded px-1.5 py-0.5 text-[10px] ${st === "open" ? "bg-rose-500/15 text-rose-300" : st === "reviewed" ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{st}</span>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1.5">
                      {st !== "reviewed" && <button data-testid={`clash-review-${i}`} onClick={() => resolveClash(c.clash_key, "reviewed", noteVal)} className="rounded border border-amber-600/50 px-2 py-0.5 text-[10px] text-amber-300 hover:bg-amber-500/10">Reviewed</button>}
                      {st !== "resolved" && <button data-testid={`clash-resolve-${i}`} onClick={() => resolveClash(c.clash_key, "resolved", noteVal)} className="rounded border border-emerald-600/50 px-2 py-0.5 text-[10px] text-emerald-300 hover:bg-emerald-500/10">Resolve</button>}
                      {st !== "open" && <button data-testid={`clash-reopen-${i}`} onClick={() => resolveClash(c.clash_key, "open", noteVal)} className="rounded border border-slate-700 px-2 py-0.5 text-[10px] text-slate-400 hover:text-white">Reopen</button>}
                    </div>
                  )}
                </div>
                {canEdit ? (
                  <input data-testid={`clash-note-${i}`} value={noteVal}
                    onChange={(e) => setClashNotes((n) => ({ ...n, [c.clash_key]: e.target.value }))}
                    placeholder="Resolution note (e.g. re-routed duct 6in)"
                    className="mt-1.5 w-full rounded border border-slate-800 bg-[#0d1420] px-2 py-1 text-[11px] text-slate-300 placeholder:text-slate-600 focus:border-cyan-600 focus:outline-none" />
                ) : c.note ? <p className="mt-1 text-[11px] text-slate-500" data-testid={`clash-note-view-${i}`}>note: {c.note}</p> : null}
              </div>
            );
          })}
        </div>
      )}
      </>)}

      {activeView === "construction" && <ProjectConstruction projectId={id} canEdit={canEdit} />}
      {activeView === "post_construction" && <ProjectHandover projectId={id} canEdit={canEdit} onChange={refresh} />}
    </div>
  );
}
